import { Stack, Text } from '@mantine/core';

import logger from '@ayocore/platform/common/helpers/logger';
import { REPOSITORIES } from '@ayocore/platform/common/types/repositories.generated';
import { telephonyCallLinkRepository } from '@ayocore/telephony/client/rpc';
import type { TelephonyCallRow } from '@ayocore/telephony/client/extensions/types';

import { CreateLeadForm } from '../components/Leads/forms/CreateLeadForm';
import { leadRepository } from '../rpc';

interface CreateLeadFromCallProps {
  call: TelephonyCallRow;
  onCreated: () => void;
}

/** Номер звонившего в читаемом виде. */
function callerNumber(call: TelephonyCallRow): string {
  return call.from_number ?? call.from_raw_number ?? 'неизвестный номер';
}

/**
 * Заведение лида по входящему звонку.
 *
 * Тонкая обёртка над штатной формой: своя копия разошлась бы с ней при первой
 * же правке полей лида. Обёртка отвечает за две вещи — подставить то, что
 * известно из звонка, и замкнуть круг обратной привязкой.
 */
export function CreateLeadFromCall({ call, onCreated }: CreateLeadFromCallProps) {
  const number = callerNumber(call);

  /**
   * Привязать звонок к созданному лиду.
   *
   * Без этого шага звонок остался бы в «Нераспределённых» рядом с лидом,
   * который из него же и вырос. Неудача привязки не отменяет создание лида —
   * он уже есть, и терять его из-за второстепенного шага нельзя.
   */
  const linkCallToLead = async (leadId?: number): Promise<void> => {
    try {
      if (leadId) {
        const lead = await leadRepository.findById(leadId);

        if (lead?.uuid) {
          await telephonyCallLinkRepository.linkManually(
            call.id,
            REPOSITORIES.CRM_LEAD,
            lead.uuid,
            lead.title ?? `Лид по звонку с ${number}`
          );
        }
      }
    } catch (error) {
      logger.error('[crm-telephony] Лид создан, но звонок к нему не привязался', {
        callId: call.id,
        leadId,
        error,
      });
    } finally {
      onCreated();
    }
  };

  return (
    <Stack gap='sm'>
      <Text size='sm' c='dimmed'>
        Входящий звонок с {number}
        {call.from_name ? ` · ${call.from_name}` : ''}
      </Text>

      <CreateLeadForm
        onLeadCreated={linkCallToLead}
        initialData={{
          title: `Звонок с ${number}`,
          // Штатные поля источника CRM: по ним видно, откуда взялся лид,
          // без выдумывания собственного способа это записать
          source_entity_type: REPOSITORIES.TELEPHONY_TELEPHONY_CALL,
          source_entity_uuid: call.uuid,
        }}
      />
    </Stack>
  );
}

export default CreateLeadFromCall;
